/**
 * MarkdownRenderer — Agent chat / 위젯 공용 마크다운 렌더러
 *
 * 의존성 없는 경량 파서 + React 컴포넌트 기반 렌더링.
 * `react-markdown` 미설치 환경에서 사용.
 *
 * 지원 문법:
 * - Headings: `#`, `##`, `###` (최대 3단계)
 * - Bold / Italic / Code: `**bold**`, `*italic*`, `` `code` ``
 * - Links: `[text](url)` — 외부 링크는 _blank
 * - Lists: `-`, `*`, `+` (unordered), `1.` (ordered) — `<ul>`/`<ol>` 래핑
 * - Code blocks: ` ```lang ... ``` `
 * - Blockquote: `> text`
 * - HR: `---`
 * - Table (GFM): `| col1 | col2 |`
 * - Inline citation: `[1]`, `[2]` (Citation 배열 제공 시 interactive)
 *
 * Streaming-safe:
 * - 부분적으로 닫히지 않은 code block은 open 상태로 그대로 렌더
 * - 최신 버퍼가 추가되어도 기존 블록은 그대로 (React reconciliation)
 */

import { useMemo } from "react";
import { cn } from "@/shared/lib/utils";
import { InlineCitation } from "@/agent/ui/chat/inline-citation";
import type { Citation } from "@modootoday/core-agent/types";

/* ══════════════════════════════════════════════════════════════════════════
   Block AST
   ══════════════════════════════════════════════════════════════════════════ */

type MarkdownBlock =
  | { kind: "h1"; text: string }
  | { kind: "h2"; text: string }
  | { kind: "h3"; text: string }
  | { kind: "blockquote"; text: string }
  | { kind: "code_block"; lang: string; code: string }
  | { kind: "list_item"; text: string; ordered: boolean; num?: number }
  | { kind: "hr" }
  | { kind: "paragraph"; text: string }
  | {
      kind: "table";
      headers: string[];
      rows: string[][];
      aligns: Array<"left" | "center" | "right" | null>;
    }
  | { kind: "blank" };

/* ══════════════════════════════════════════════════════════════════════════
   parseBlocks — 마크다운 소스 → 블록 배열
   ══════════════════════════════════════════════════════════════════════════ */

export function parseBlocks(markdown: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  const lines = markdown.split("\n");
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? "";

    // Fenced code block
    if (/^```/.test(line)) {
      const lang = line.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i] ?? "")) {
        codeLines.push(lines[i] ?? "");
        i++;
      }
      blocks.push({ kind: "code_block", lang, code: codeLines.join("\n") });
      i++; // skip closing ```
      continue;
    }

    // Headings (3단계까지)
    const h3 = line.match(/^### (.+)/);
    if (h3) {
      blocks.push({ kind: "h3", text: h3[1]! });
      i++;
      continue;
    }
    const h2 = line.match(/^## (.+)/);
    if (h2) {
      blocks.push({ kind: "h2", text: h2[1]! });
      i++;
      continue;
    }
    const h1 = line.match(/^# (.+)/);
    if (h1) {
      blocks.push({ kind: "h1", text: h1[1]! });
      i++;
      continue;
    }

    // HR
    if (/^---+$/.test(line.trim()) || /^\*\*\*+$/.test(line.trim())) {
      blocks.push({ kind: "hr" });
      i++;
      continue;
    }

    // Blockquote (연속 라인 병합)
    if (/^>\s*/.test(line)) {
      const bqLines: string[] = [];
      while (i < lines.length && /^>\s*/.test(lines[i] ?? "")) {
        bqLines.push((lines[i] ?? "").replace(/^>\s*/, ""));
        i++;
      }
      blocks.push({ kind: "blockquote", text: bqLines.join("\n") });
      continue;
    }

    // Table (GFM) — | col1 | col2 |\n| --- | --- |\n| ... |
    if (/^\s*\|.+\|\s*$/.test(line)) {
      const nextLine = lines[i + 1] ?? "";
      if (
        /^\s*\|?[\s:|-]+\|[\s:|-]+\|?\s*$/.test(nextLine) &&
        nextLine.includes("-")
      ) {
        // 테이블 감지
        const headers = parseTableRow(line);
        const aligns = parseTableAligns(nextLine);
        const rows: string[][] = [];
        i += 2;
        while (i < lines.length && /^\s*\|.+\|\s*$/.test(lines[i] ?? "")) {
          rows.push(parseTableRow(lines[i] ?? ""));
          i++;
        }
        blocks.push({ kind: "table", headers, rows, aligns });
        continue;
      }
    }

    // Unordered list
    const ul = line.match(/^[-*+]\s+(.*)/);
    if (ul) {
      blocks.push({ kind: "list_item", text: ul[1]!, ordered: false });
      i++;
      continue;
    }

    // Ordered list
    const ol = line.match(/^(\d+)\.\s+(.*)/);
    if (ol) {
      blocks.push({
        kind: "list_item",
        text: ol[2]!,
        ordered: true,
        num: parseInt(ol[1]!, 10),
      });
      i++;
      continue;
    }

    // Blank line
    if (line.trim() === "") {
      blocks.push({ kind: "blank" });
      i++;
      continue;
    }

    // Paragraph — 연속 비어있지 않은 특수 패턴이 아닌 라인 수집
    const paraLines: string[] = [line];
    i++;
    while (
      i < lines.length &&
      (lines[i] ?? "").trim() !== "" &&
      !/^(#{1,3} |```|>|[-*+] |\d+\. |---|\s*\|)/.test(lines[i] ?? "")
    ) {
      paraLines.push(lines[i] ?? "");
      i++;
    }
    blocks.push({ kind: "paragraph", text: paraLines.join("\n") });
  }

  return blocks;
}

function parseTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

function parseTableAligns(
  separatorLine: string,
): Array<"left" | "center" | "right" | null> {
  return parseTableRow(separatorLine).map((cell) => {
    const t = cell.trim();
    if (/^:-+:$/.test(t)) return "center";
    if (/^:-+$/.test(t)) return "left";
    if (/^-+:$/.test(t)) return "right";
    return null;
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   parseInline — 인라인 마크다운 (bold/italic/code/link + Citation)
   ══════════════════════════════════════════════════════════════════════════ */

interface InlineContext {
  citations?: Citation[];
  onDeepLink?: (path: string) => void;
  keyPrefix?: string;
}

export function parseInline(
  text: string,
  ctx: InlineContext = {},
): React.ReactNode[] {
  const segments: React.ReactNode[] = [];
  const { citations = [], onDeepLink, keyPrefix = "i" } = ctx;

  // 통합 패턴: **bold**, *italic*, `code`, [text](url), [N] (citation)
  // 단, [N]은 citations가 있을 때만 특별 처리
  const pattern =
    /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)|\[(\d+)\])/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let keyIdx = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push(text.slice(lastIndex, match.index));
    }

    const key = `${keyPrefix}-${keyIdx++}`;

    if (match[2] !== undefined) {
      // **bold**
      segments.push(
        <strong key={key} className="font-semibold text-text-primary">
          {match[2]}
        </strong>,
      );
    } else if (match[3] !== undefined) {
      // *italic*
      segments.push(
        <em key={key} className="italic">
          {match[3]}
        </em>,
      );
    } else if (match[4] !== undefined) {
      // `code`
      segments.push(
        <code
          key={key}
          className="rounded bg-surface-secondary px-1 py-0.5 font-mono text-[11px] text-text-primary"
        >
          {match[4]}
        </code>,
      );
    } else if (match[5] !== undefined && match[6] !== undefined) {
      // [text](url)
      segments.push(
        <a
          key={key}
          href={match[6]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-text-brand underline underline-offset-2 hover:opacity-80"
          onClick={(e) => e.stopPropagation()}
        >
          {match[5]}
        </a>,
      );
    } else if (match[7] !== undefined) {
      // [N] Citation 마커
      const citationIdx = parseInt(match[7], 10);
      const citation = citations[citationIdx - 1];
      if (citation) {
        segments.push(
          <InlineCitation
            key={key}
            index={citationIdx}
            citation={citation}
            onDeepLink={onDeepLink}
          />,
        );
      } else {
        segments.push(match[0]);
      }
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    segments.push(text.slice(lastIndex));
  }

  return segments;
}

/* ══════════════════════════════════════════════════════════════════════════
   Block Renderer
   ══════════════════════════════════════════════════════════════════════════ */

interface RenderBlockProps {
  block: MarkdownBlock;
  idx: number;
  ctx: InlineContext;
}

function RenderBlock({ block, idx, ctx }: RenderBlockProps) {
  const inlineCtx: InlineContext = { ...ctx, keyPrefix: `b${idx}` };

  switch (block.kind) {
    case "h1":
      return (
        <h1 className="mt-4 mb-2 text-[17px] font-bold leading-snug text-text-primary first:mt-0">
          {parseInline(block.text, inlineCtx)}
        </h1>
      );
    case "h2":
      return (
        <h2 className="mt-3.5 mb-1.5 text-[15px] font-semibold leading-snug text-text-primary first:mt-0">
          {parseInline(block.text, inlineCtx)}
        </h2>
      );
    case "h3":
      return (
        <h3 className="mt-3 mb-1 text-[13px] font-semibold text-text-primary first:mt-0">
          {parseInline(block.text, inlineCtx)}
        </h3>
      );

    case "blockquote":
      return (
        <blockquote className="my-2 border-l-2 border-accent/50 pl-3 text-[12px] italic text-text-tertiary">
          {parseInline(block.text, inlineCtx)}
        </blockquote>
      );

    case "code_block":
      return (
        <div className="my-2 overflow-x-auto rounded-lg bg-surface-secondary/70">
          {block.lang && (
            <div className="border-b border-stroke-subtle px-3 py-1 text-[10px] font-mono text-text-tertiary">
              {block.lang}
            </div>
          )}
          <pre className="p-3 text-[11px] leading-relaxed">
            <code className="font-mono text-text-primary">{block.code}</code>
          </pre>
        </div>
      );

    case "list_item": {
      return (
        <li className="flex gap-2 text-[13px] leading-relaxed text-text-primary/90">
          {block.ordered ? (
            <span className="shrink-0 font-medium text-text-tertiary tabular-nums min-w-[1.5em]">
              {block.num}.
            </span>
          ) : (
            <span className="mt-2 size-1.5 shrink-0 rounded-full bg-text-tertiary" />
          )}
          <span className="flex-1">{parseInline(block.text, inlineCtx)}</span>
        </li>
      );
    }

    case "hr":
      return <hr className="my-3 border-stroke-subtle" />;

    case "blank":
      return null; // 블록 간격은 space-y-*에서 처리

    case "paragraph":
      return (
        <p className="text-[13px] leading-relaxed text-text-primary/90 whitespace-pre-wrap">
          {parseInline(block.text, inlineCtx)}
        </p>
      );

    case "table":
      return (
        <div className="my-2 overflow-x-auto rounded-lg border border-stroke-subtle">
          <table className="w-full border-collapse text-[12px]">
            <thead className="bg-surface-secondary/60">
              <tr>
                {block.headers.map((h, hi) => (
                  <th
                    key={hi}
                    className={cn(
                      "border-b border-stroke-subtle px-3 py-2 font-semibold text-text-primary",
                      block.aligns[hi] === "center" && "text-center",
                      block.aligns[hi] === "right" && "text-right",
                      (!block.aligns[hi] || block.aligns[hi] === "left") &&
                        "text-left",
                    )}
                  >
                    {parseInline(h, { ...inlineCtx, keyPrefix: `th${hi}` })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr
                  key={ri}
                  className={cn(
                    "border-b border-stroke-subtle/50 last:border-0",
                    ri % 2 === 1 && "bg-surface-secondary/20",
                  )}
                >
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className={cn(
                        "px-3 py-1.5 text-text-primary/90",
                        block.aligns[ci] === "center" && "text-center",
                        block.aligns[ci] === "right" &&
                          "text-right tabular-nums",
                      )}
                    >
                      {parseInline(cell, {
                        ...inlineCtx,
                        keyPrefix: `td${ri}-${ci}`,
                      })}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      return null;
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   List grouping — 연속된 list_item을 <ul>/<ol>로 감싸기
   ══════════════════════════════════════════════════════════════════════════ */

function renderBlocksWithListGroups(
  blocks: MarkdownBlock[],
  ctx: InlineContext,
): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < blocks.length) {
    const block = blocks[i]!;

    if (block.kind === "list_item") {
      const listItems: MarkdownBlock[] = [];
      const isOrdered = block.ordered;
      const startIdx = i;
      while (
        i < blocks.length &&
        blocks[i]!.kind === "list_item" &&
        (blocks[i] as { ordered: boolean }).ordered === isOrdered
      ) {
        listItems.push(blocks[i]!);
        i++;
      }
      const Tag = isOrdered ? "ol" : "ul";
      elements.push(
        <Tag key={`list-${startIdx}`} className="my-1.5 space-y-0.5">
          {listItems.map((item, liIdx) => (
            <RenderBlock
              key={liIdx}
              block={item}
              idx={startIdx + liIdx}
              ctx={ctx}
            />
          ))}
        </Tag>,
      );
    } else {
      elements.push(<RenderBlock key={i} block={block} idx={i} ctx={ctx} />);
      i++;
    }
  }

  return elements;
}

/* ══════════════════════════════════════════════════════════════════════════
   MarkdownRenderer Component
   ══════════════════════════════════════════════════════════════════════════ */

export interface MarkdownRendererProps {
  /** 마크다운 소스 텍스트 */
  content: string;
  /** 인라인 [N] 마커를 클릭 가능한 출처로 변환 */
  citations?: Citation[];
  /** 링크/출처 클릭 핸들러 */
  onDeepLink?: (path: string) => void;
  className?: string;
}

export function MarkdownRenderer({
  content,
  citations,
  onDeepLink,
  className,
}: MarkdownRendererProps) {
  const blocks = useMemo(() => parseBlocks(content), [content]);

  const ctx: InlineContext = {
    citations,
    onDeepLink,
  };

  return (
    <div className={cn("space-y-1 text-[13px] leading-relaxed", className)}>
      {renderBlocksWithListGroups(blocks, ctx)}
    </div>
  );
}
